import React from 'react';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import { StreamOptions } from '@smart-tv/components/player/vod-wrapper/_contexts/stream-player-context';
import useVodPlayer from '@smart-tv/components/player/vod-wrapper/_hooks/use-vod-player';
import useDialog from '@smart-tv/hooks/use-dialog/use-dialog';
import useQuery from '@smart-tv/hooks/use-query';
import { ContentType, StreamType } from '@smart-tv/typings/content';

import isWithinInterval from '@smart-tv/utils/date/is-within-interval';
import decodeProgramSheetParams from '@smart-tv/utils/routing/decode-program-sheet-params';

import { QueryParams } from '../components/sheet';
import { SESSION_STORAGE_SHEET_KEY } from '../_constants/session-storage';
import { rootStore } from './store-root';
import { uiStore } from './store-ui';

export const useStreamActions = () => {
  const { sheet } = rootStore.sheetStore;
  const { providersVersionsPickerModel } = rootStore.providerVersionStore;

  const { programsheet } = useQuery<QueryParams>();
  const { handleOpen } = useVodPlayer();
  const { onSetPlayerFocusKey } = usePlayerContext();
  const { closedDialogFocusKey: dialogFocusKey } = useDialog();
  const { sid } = rootStore.sheetStore.params;

  const openStream = React.useCallback(
    (streamOptions: StreamOptions) => {
      rootStore.episodesStore.cancelPendingFetch();
      handleOpen(streamOptions);
    },
    [handleOpen]
  );

  const storeSheetParametersInSessionStorage = React.useCallback(() => {
    const {
      sid = 0,
      eventId = 0,
      contentId = 0,
      contentType = typeof ContentType
    } = decodeProgramSheetParams(programsheet);

    const subProviderId = rootStore.sheetStore.params.subProviderId;

    if (sessionStorage.getItem(SESSION_STORAGE_SHEET_KEY)) {
      sessionStorage.removeItem(SESSION_STORAGE_SHEET_KEY);
    }

    if (sid && eventId && sid !== 0 && eventId !== 0) {
      const { sid, eventId } = sheet.providers[0].versions[0];
      sessionStorage.setItem(
        SESSION_STORAGE_SHEET_KEY,
        JSON.stringify({ sid, eventId, subProviderId })
      );
    } else if (contentId && contentId !== 0 && contentType) {
      sessionStorage.setItem(
        SESSION_STORAGE_SHEET_KEY,
        JSON.stringify({ contentId, contentType, subProviderId })
      );
    }
  }, [programsheet, sheet.providers]);

  const handleStartVod = React.useCallback(
    (isStartOver = false, selectedVersionIndex?: number) => {
      const {
        sid,
        eventId,
        mediaId,
        secondsWatched: progress,
        forceStartOver
      } = providersVersionsPickerModel[selectedVersionIndex ?? 0];

      const finalProgress = isStartOver || forceStartOver ? 0 : progress;

      const streamOptions: StreamOptions = {
        streamType: StreamType.Vod,
        streamParams: mediaId
          ? { mediaId, progress: finalProgress }
          : { sid, eventId, progress: finalProgress }
      };
      openStream(streamOptions);
    },
    [openStream, providersVersionsPickerModel]
  );

  const handleCatchUp = React.useCallback(
    (isStartOver = false, selectedVersionIndex?: number) => {
      const {
        sid,
        eventId,
        mediaId,
        secondsWatched: progress,
        airTimeStart,
        forceStartOver
      } = providersVersionsPickerModel[selectedVersionIndex ?? 0];
      const streamOptions: StreamOptions = {
        startTime: airTimeStart ? new Date(airTimeStart * 1000) : undefined,
        streamParams: mediaId
          ? { mediaId, progress: isStartOver || forceStartOver ? 0 : progress }
          : { sid, eventId, progress: isStartOver || forceStartOver ? 0 : progress },
        streamType: StreamType.CatchUp
      };
      openStream(streamOptions);
    },
    [openStream, providersVersionsPickerModel]
  );

  const handleOpenLive = React.useCallback(() => {
    if (sheet && sid) {
      onSetPlayerFocusKey(dialogFocusKey.current);
      const streamOptions: StreamOptions = {
        streamType: StreamType.LiveStream,
        streamParams: { sid }
      };
      openStream(streamOptions);
    }
  }, [sheet, onSetPlayerFocusKey, dialogFocusKey, sid, openStream]);

  const handlePlay = (selectedVersionIndex?: number) => {
    storeSheetParametersInSessionStorage();

    const versionIndex = selectedVersionIndex ?? 0;

    if (providersVersionsPickerModel.length > 1 && selectedVersionIndex === undefined) {
      // If there are multiple provider versions, show the providers versions dialog
      uiStore.toggleProvidersVersionsDialog(true);
      return;
    }

    if (!providersVersionsPickerModel[versionIndex]) return;

    const isFlowTvLive = () => {
      const startTime = providersVersionsPickerModel[versionIndex].airTime;
      const length = providersVersionsPickerModel[versionIndex].length * 1000;
      const endTime = startTime + length;

      return isWithinInterval(Date.now(), { start: startTime, end: endTime });
    };

    const version = providersVersionsPickerModel[versionIndex];

    const isLive = isFlowTvLive();

    if (version.mediaId) {
      handleStartVod(false, versionIndex);
      return;
    }

    if (isLive) {
      handleOpenLive();
    } else {
      handleCatchUp(false, versionIndex);
    }
  };

  const handleStartOver = React.useCallback(
    (selecteVersionIndex?: number) => {
      const versionIndex = selecteVersionIndex ?? 0;
      storeSheetParametersInSessionStorage();
      const mediaId = providersVersionsPickerModel[versionIndex].mediaId;

      if (providersVersionsPickerModel.length > 1 && selecteVersionIndex === undefined) {
        // If there are multiple provider versions, show the providers versions dialog
        uiStore.toggleProvidersVersionsDialog(true);
        uiStore.toggleInvokeStartOver(true);
        return;
      }

      const startTime = providersVersionsPickerModel[versionIndex].airTime;
      const length = providersVersionsPickerModel[versionIndex].length * 1000;
      const endTime = startTime + length;

      const isLive = isWithinInterval(Date.now(), { start: startTime, end: endTime });

      if (mediaId) {
        handleStartVod(true, versionIndex);
        return;
      }

      if (!isLive) handleCatchUp(true, versionIndex);

      if (isLive && sid) {
        const { eventId } = providersVersionsPickerModel[versionIndex];
        const streamOptions: StreamOptions = {
          streamType: StreamType.StartOver,
          streamParams: { sid, eventId }
        };
        openStream(streamOptions);
      }
    },
    [
      handleCatchUp,
      handleStartVod,
      openStream,
      providersVersionsPickerModel,
      sid,
      storeSheetParametersInSessionStorage
    ]
  );

  return {
    handlePlay,
    handleStartOver
  };
};
